import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { apiClient } from '../../api';
import { AdminDataGrid } from '../../components/data-grid';
import { formatCurrency } from '../../utils/format';

/**
 * Health smoke page — hits `GET /health` on the configured API base
 * URL and renders a verdict. Useful in dev: shows ✅ when the API is
 * up, ❌ when it's down. Doubles as a manual cross-origin check.
 *
 * Real /admin/queue-counts polling + WS connectivity badges land later
 * with ADMIN-15+; v1 keeps it to one fetch.
 */
type HealthState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok' }
  | { status: 'down'; reason: string };

/** Static demo rows exercising the shared `<AdminDataGrid>` (ADMIN-10 smoke). */
interface DemoRow {
  id: string;
  name: string;
  status: string;
  amount: number;
}

const DEMO_ROWS: DemoRow[] = [
  { id: 'ord-1001', name: 'BMW X5', status: 'paid', amount: 149.5 },
  { id: 'ord-1002', name: 'Audi A6', status: 'in_progress', amount: 89 },
  { id: 'ord-1003', name: 'VW Passat', status: 'completed', amount: 120 },
];

const DataGridDemo = (): ReactElement => {
  const { t } = useTranslation();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const columns = useMemo<GridColDef<DemoRow>[]>(
    () => [
      { field: 'id', headerName: t('dev.colId'), width: 140 },
      { field: 'name', headerName: t('dev.colName'), flex: 1 },
      { field: 'status', headerName: t('dev.colStatus'), width: 160 },
      {
        field: 'amount',
        headerName: t('dev.colAmount'),
        width: 140,
        valueFormatter: (value: number) => formatCurrency(value),
      },
    ],
    [t],
  );

  return (
    <Box sx={{ mt: 6 }}>
      <Typography component="h2" variant="h2" sx={{ mb: 2 }}>
        {t('dev.gridDemoTitle')}
      </Typography>
      <AdminDataGrid<DemoRow>
        rows={DEMO_ROWS}
        columns={columns}
        rowCount={DEMO_ROWS.length}
        loading={false}
        error={null}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
      />
    </Box>
  );
};

export const HealthPage = (): ReactElement => {
  const { t } = useTranslation();
  const [state, setState] = useState<HealthState>({ status: 'idle' });

  const check = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      await apiClient.get('/health');
      setState({ status: 'ok' });
    } catch (error: unknown) {
      const reason = error instanceof Error ? error.message : 'unknown';
      setState({ status: 'down', reason });
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  return (
    <Box>
      <Typography component="h1" variant="h1" sx={{ mb: 4 }}>
        {t('health.title')}
      </Typography>
      {state.status === 'loading' || state.status === 'idle' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">{t('health.checking')}</Typography>
        </Box>
      ) : null}
      {state.status === 'ok' ? <Alert severity="success">{t('health.ok')}</Alert> : null}
      {state.status === 'down' ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('health.down')} — <code>{state.reason}</code>
        </Alert>
      ) : null}
      <Button variant="outlined" onClick={() => void check()} sx={{ mt: 4 }}>
        {t('common.retry')}
      </Button>
      {import.meta.env.DEV ? <DataGridDemo /> : null}
    </Box>
  );
};
